import React, { useCallback, useContext, useState, useEffect, ChangeEvent } from 'react';

import { IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';

import { GlobalContext } from '../../../store';
import { ITeam } from '../../../store/admin/eventTeams/store';

import {
  useTeamsCreatorDispatch,
  useTeamsCreatorState
} from '../../../scenes/admin/EventWizard/TeamsCreator/Context/TeamsCreator.context';
import { useBoolean } from '../../../scenes/common/Hooks/useBoolean.hook';
import Button from '../../Button';
import { RemoveModal } from '../RemoveTeamModal';
import { TeamCardActionsStyled } from './TeamCardActions.Styles';
import { useRouteInfoState } from '../../RouteInfo/Context/RouteInfo.context';
import { TTeamCardContext } from '../TeamCard';
import { IAdminTeamGame } from '../../../store/admin/teamGames/store';
import Input from '../../Input';

interface IProps {
  team: ITeam;
  context: TTeamCardContext;
  hideActions?: boolean;
  teamGame?: IAdminTeamGame;
}

export const TeamCardActions: React.FunctionComponent<IProps> = props => {
  const { team, context, teamGame, hideActions } = props;

  const {
    actions: {
      adminEventTeams: { delete: deleteAdminEventTeam, update: updateAdminEventTeam },
      adminTeamGames: { delete: deleteTeamGame },
      adminEventRounds: { get: getRound }
    }
  } = useContext(GlobalContext);

  const { clubId, eventId, roundId } = useRouteInfoState();

  const { editMode, selectedTeam, courseHandicap } = useTeamsCreatorState();

  const { dispatch: dispatchTeamsCreator } = useTeamsCreatorDispatch();

  const [teamName, setTeamName] = useState('');
  const [thisTeamIsSelected, setThisTeamIsSelected] = useState(false);
  const [removeModalIsOpen, , toggleRemoveModalIsOpen] = useBoolean(false);

  useEffect(() => {
    setTeamName(team.customName.trim().length > 0 ? team.customName : `TEAM ${team.order + 1}`);
  }, [team]);

  useEffect(() => {
    setThisTeamIsSelected(!!selectedTeam && selectedTeam.id === team.id);
  }, [selectedTeam, team]);

  const onSelectTeam = () =>
    dispatchTeamsCreator({
      type: 'selectTeam',
      value: team
    });

  const cancelEditMode = async () => {
    dispatchTeamsCreator({ type: 'cancelEditMode' });

    if (teamName !== team.customName) {
      await updateAdminEventTeam(clubId, eventId, team.id, { customName: teamName });
    }

    if (courseHandicap) {
      await updateAdminEventTeam(clubId, eventId, team.id, { courseHandicap });

      dispatchTeamsCreator({ type: 'setCourseHandicap', value: undefined });
    }
  };

  const onRemoveTeamFromTee = async () => {
    if (teamGame) {
      await deleteTeamGame(clubId, eventId, roundId, teamGame);
      await getRound(Number(clubId), Number(eventId), Number(roundId), true);

      toggleRemoveModalIsOpen();
    }
  };

  const onRemoveTeam = useCallback(async () => {
    if (context === 'teams') {
      if (team.id > 0) {
        await deleteAdminEventTeam(clubId, eventId, team.id);
        return;
      } else {
        dispatchTeamsCreator({ type: 'setDisplayNewTeam', value: false });
        dispatchTeamsCreator({ type: 'resetNewTeam' });
        return;
      }
    }

    if (context.includes('tee')) {
      await onRemoveTeamFromTee();
    }
  }, [team]);

  const onChangeTeamName = useCallback(
    (evnt: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setTeamName(evnt.target.value);
    },
    [setTeamName]
  );

  return (
    <TeamCardActionsStyled>
      {editMode && thisTeamIsSelected && context === 'teams' ? (
        <Input
          type="text"
          value={teamName}
          onChange={onChangeTeamName}
          fullWidth={false}
          noIcon={true}
        />
      ) : (
        <h1 className={`title ${editMode ? 'edit-mode' : ''}`}>{teamName}</h1>
      )}

      {!hideActions && (
        <>
          {editMode && thisTeamIsSelected ? (
            <>
              <Button
                color="primary"
                variant="text"
                onClick={toggleRemoveModalIsOpen}
                aria-label="cancel"
                id="close-button"
              >
                Delete
              </Button>

              <Button color="primary" variant="contained" onClick={cancelEditMode}>
                Done
              </Button>
            </>
          ) : (
            <IconButton
              color="primary"
              onClick={onSelectTeam}
              aria-label="cancel"
              id="close-button"
            >
              <EditIcon />
            </IconButton>
          )}
        </>
      )}
      <RemoveModal
        modalIsOpen={removeModalIsOpen}
        toggleModalIsOpen={toggleRemoveModalIsOpen}
        onRemove={onRemoveTeam}
        resourceName="Team"
      />
    </TeamCardActionsStyled>
  );
};
